import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { Record1099B } from "./interface/1099-B-interface";
import {
  generateRandomRecordId,
  generateUniqueSubmissionId,
  generateUniqueTransmissionId,
} from "../../constants/genRecordId";
import { v4 as uuidv4 } from "uuid";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { parseAmount } from "../../constants/validationFunctions";
import { formatDate } from "../../constants/formatDate";
import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";

/**
 * Helper function to format SoftwareId to ensure first two digits match tax year
 *
 * According to IRS documentation:
 * - New Software IDs are assigned annually for each tax year
 * - Software IDs must start with the last two digits of the tax year (e.g., "25" for 2025)
 * - The Software ID assigned by IRS for 2025 should already have this format
 *
 * This function acts as a safety check to ensure the Software ID matches the tax year.
 * Note: You should use the Software ID assigned by IRS for the current tax year.
 *
 * @param softwareId - The Software ID from environment variable (should be the IRS-assigned ID for the tax year)
 * @param taxYear - The tax year (e.g., "2025")
 * @returns Formatted Software ID with first two digits matching the tax year
 */
function formatSoftwareId(
  softwareId: string | undefined,
  taxYear: string
): string {
  // Get last two digits of tax year (e.g., "25" for 2025)
  const yearSuffix = taxYear.slice(-2);

  // If no software ID provided, return default with year prefix
  // Note: This is a fallback - you should always provide the IRS-assigned Software ID
  if (!softwareId) {
    console.warn(
      `No Software ID provided. Using default. Please use the IRS-assigned Software ID for tax year ${taxYear}.`
    );
    return `${yearSuffix}00000`;
  }

  // If SoftwareId already starts with correct year, return as-is
  // This should be the case for properly assigned Software IDs
  if (softwareId.startsWith(yearSuffix)) {
    return softwareId.substring(0, 10); // Max 10 characters per schema
  }

  // If SoftwareId doesn't start with correct year, warn and fix it
  // This handles cases where an old Software ID from a previous year might be used
  console.warn(
    `Software ID "${softwareId}" does not start with "${yearSuffix}" for tax year ${taxYear}. ` +
      `Updating to match tax year. Please ensure you're using the IRS-assigned Software ID for ${taxYear}.`
  );

  // Replace first two characters with year suffix
  // Keep remaining characters (up to 10 total length)
  const remaining = softwareId.substring(2);
  return `${yearSuffix}${remaining}`.substring(0, 10);
}

/**
 * Clean address string to match IRS StreetAddressType pattern
 * Pattern: [A-Za-z0-9]( ?[A-Za-z0-9\-/])*
 * Removes invalid characters like #, &, etc.
 */
function cleanAddress(address: string): string {
  if (!address) return address;

  // Replace # with UNIT, & with AND, remove other invalid characters
  return address
    .replace(/#/g, "UNIT ")
    .replace(/&/g, "AND")
    .replace(/[^A-Za-z0-9\s\-/]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export async function generate1099BXml(
  ctx: EngineContext,records: Record1099B[]) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    throw new Error("No records found or records input is invalid.");
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;

  // Initialize the XML builder.
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

  // Determine tax year from the first record.
  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);

  // Build an array of form details, one for each record.
  const form1099BDetails = records.map((record) => {
    // Safely handle string operations to avoid trim errors
    const safePersonNameControl = record.RECIPIENT_NAME1
      ? (record.RECIPIENT_NAME1.substring(0, 4) || "").toUpperCase()
      : "";

    const safeLastName = record.RECIPIENT_NAME2 || "UNKNOWN";
    const safeCity = record.RECIPIENT_CITY;

    return {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
      TaxYr: taxYear.toString(),
      RecordId: record.id.toString(),
      VoidInd: "0",
      CorrectedInd: record.corrected ? "1" : "0",
      // TS-8174: TY2026 layout-V2 records file via the shared split builder
      // (business as BusinessName+BUSINESS_TIN, individuals as split
      // PersonName) — the legacy RECIPIENT_NAME1/NAME2 shape below emitted
      // PersonName groups missing PersonFirstNm for 2026 rows and failed XSD.
      RecipientDetail:
        (buildSplitRecipientDetail(
          record as any,
          transmitterData.testFileIndicator === "T"
            ? "000000000"
            : record.RECIPIENT_ID_NUMBER.replace(/-/g, ""),
          {
            USAddress: {
              ...(record.RECIPIENT_ADDRESS1 && {
                AddressLine1Txt: cleanAddress(record.RECIPIENT_ADDRESS1),
              }),
              ...(safeCity && { CityNm: safeCity }),
              ...{ StateAbbreviationCd: record.RECIPIENT_STATE },
              ...(record.RECIPIENT_ZIP && {
                ZIPCd: record.RECIPIENT_ZIP,
              }),
            },
          }
        ) as any) ?? {
        TIN:
          transmitterData.testFileIndicator === "T"
            ? "000000000"
            : record.RECIPIENT_ID_NUMBER.replace(/-/g, ""),
        TINSubmittedTypeCd: "INDIVIDUAL_TIN",
        ...(safePersonNameControl && {
          PersonNameControlTxt: safePersonNameControl,
        }),
        PersonName: {
          PersonFirstNm: record.RECIPIENT_NAME1,
          PersonLastNm: safeLastName,
        },
        MailingAddressGrp: {
          USAddress: {
            ...(record.RECIPIENT_ADDRESS1 && {
              AddressLine1Txt: cleanAddress(record.RECIPIENT_ADDRESS1),
            }),
            ...(safeCity && { CityNm: safeCity }),
            ...{ StateAbbreviationCd: record.RECIPIENT_STATE },
            ...(record.RECIPIENT_ZIP && {
              ZIPCd: record.RECIPIENT_ZIP,
            }),
          },
        },
      },
      ...(record.ACCOUNT_NUMBER && {
        RecipientAccountNum: record.ACCOUNT_NUMBER,
      }),
      SecondTINNoticeInd: "0",
      ...(record.CUSIP_NUMBER && {
        CUSIPNum: record.CUSIP_NUMBER,
      }),
      FATCAFilingRequirementInd: record.FATCA_REQUIREMENT === "X" ? "1" : "0",
      ...(record.FATCA_REQUIREMENT && {
        Form8949ApplicableCheckboxCd: record.FORM_8949_CHECKBOX,
      }),
      ...(record.BOX_1A_DESCRIPTION && {
        PropertyDesc: record.BOX_1A_DESCRIPTION,
      }),
      ...(record.BOX_1B_DATE_ACQUIRED && {
        AcquiredDt: formatDate(record.BOX_1B_DATE_ACQUIRED),
      }),
      ...(record.BOX_1C_DATE_SOLD && {
        SoldOrDisposedDt: formatDate(record.BOX_1C_DATE_SOLD),
      }),
      ...(record.BOX_1D_PROCEEDS && {
        ProceedsAmt: parseAmount(record.BOX_1D_PROCEEDS),
      }),
      ...(record.BOX_1E_COST_BASIS && {
        CostOrOtherBasisAmt: parseAmount(record.BOX_1E_COST_BASIS),
      }),
      ...(record.BOX_1F_MARKET_DISCOUNT && {
        AccruedMarketDiscountAmt: parseAmount(record.BOX_1F_MARKET_DISCOUNT),
      }),
      ...(record.BOX_1G_WASH_SALE_LOSS && {
        NondeductibleWashSaleLossAmt: parseAmount(record.BOX_1G_WASH_SALE_LOSS),
      }),
      ShortTermGainLossInd:
        record.BOX_2_SHORT_TERM_INDICATOR === "X" ? "1" : "0",
      LongTermGainLossInd: record.BOX_2_LONG_TERM_INDICATOR === "X" ? "1" : "0",
      OrdinaryInd: record.BOX_2_ORDINARY_INDICATOR === "X" ? "1" : "0",
      CollectiblesInd: record.BOX_3_PROCEEDS_COLLECTIBLES === "X" ? "1" : "0",
      QOFInd: record.BOX_3_PROCEEDS_QOF === "X" ? "1" : "0",
      ...(record.BOX_4_FEDERAL_TAX_WITHHELD && {
        FederalIncomeTaxWithheldAmt: parseAmount(
          record.BOX_4_FEDERAL_TAX_WITHHELD
        ),
      }),
      NoncoveredSecurityInd:
        record.BOX_5_NONCOVERED_SECURITY_INDICATOR === "X" ? "1" : "0",
      GrossProceedsInd:
        record.BOX_6_GROSS_PROCEEDS_INDICATOR === "X" ? "1" : "0",
      NetProceedsInd: record.BOX_6_NET_PROCEEDS_INDICATOR === "X" ? "1" : "0",
      LossNotAllowedInd:
        record.BOX_7_LOSS_NOT_ALLOWED_INDICATOR === "X" ? "1" : "0",
      ...(record.BOX_8_PROFIT_LOSS_2019 && {
        TYClosedContractProfitLossAmt: parseAmount(
          record.BOX_8_PROFIT_LOSS_2019
        ),
      }),
      ...(record.BOX_9_UNREALIZED_PL_2018 && {
        PriorYrOpenCntrctProfitLossAmt: parseAmount(
          record.BOX_9_UNREALIZED_PL_2018
        ),
      }),
      ...(record.BOX_10_UNREALIZED_PL_2019 && {
        CYOpenCntrctProfitLossAmt: parseAmount(
          record.BOX_10_UNREALIZED_PL_2019
        ),
      }),
      ...(record.BOX_11_AGGREGATE_PL && {
        CntrctAggregateProfitLossAmt: parseAmount(record.BOX_11_AGGREGATE_PL),
      }),
      BasisReportedInd:
        record.BOX_12_PROCEEDS_COLLECTIBLES_INDICATOR === "X" ? "1" : "0",
      ...(record.BOX_13_BARTERING && {
        BarterAmt: parseAmount(record.BOX_13_BARTERING),
      }),
      ...(shouldIncludeStateLocalTaxGrp(record) && {
        StateLocalTaxGrp: getStateLocalTaxGrp(record),
      }),
    };
  });

  // Generate unique identifiers
  const uniqueTransmissionId = generateUniqueTransmissionId(ctx.irs.tcc);
  const submissionId = generateUniqueSubmissionId();

  // Build the complete envelope structure.
  const xmlStructure = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: uniqueTransmissionId,
        TaxYr: taxYear,
        PriorYearDataInd: transmitterData.priorYearDataIndicator || "0",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: 1,
        TotalRecipientFormCnt: form1099BDetails.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: submissionId,
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && {
                ContactPersonNm: transmitterData.contactName,
              }),
              ...(transmitterData.contactTelephoneNumber && {
                ContactPhoneNum: transmitterData.contactTelephoneNumber,
              }),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum,
              }),
            },
            FormTypeCd: "1099B",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: "2023-04-04",
              ...(transmitterData.contactName && {
                JuratPersonTitleTxt: "CFO",
              }),
              ...(transmitterData.contactName && {
                PersonNm: transmitterData.contactName,
              }),
            },
            TotalReportedRcpntFormCnt: form1099BDetails.length.toString(),
            IRSubmission1FormTotals: {
              Form1099BTotalAmtGrp: {
                // According to the XSD schema for Form1099BTotalAmtGrp, proper order is:
                // 1. FederalIncomeTaxWithheldAmt
                FederalIncomeTaxWithheldAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX_4_FEDERAL_TAX_WITHHELD || "0"),
                    0
                  )
                  .toFixed(2),
                // 2. TotalReportedAmt (optional, can be omitted)
                // 3. ProceedsAmt
                ProceedsAmt: records
                  .reduce(
                    (sum, r) => sum + parseFloat(r.BOX_1D_PROCEEDS || "0"),
                    0
                  )
                  .toFixed(2),
                // 4. CostOrOtherBasisAmt
                CostOrOtherBasisAmt: records
                  .reduce(
                    (sum, r) => sum + parseFloat(r.BOX_1E_COST_BASIS || "0"),
                    0
                  )
                  .toFixed(2),
                // 5. AccruedMarketDiscountAmt
                AccruedMarketDiscountAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX_1F_MARKET_DISCOUNT || "0"),
                    0
                  )
                  .toFixed(2),
                // 6. NondeductibleWashSaleLossAmt
                NondeductibleWashSaleLossAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX_1G_WASH_SALE_LOSS || "0"),
                    0
                  )
                  .toFixed(2),
                // 7. Contract amounts
                TYClosedContractProfitLossAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX_8_PROFIT_LOSS_2019 || "0"),
                    0
                  )
                  .toFixed(2),
                PriorYrOpenCntrctProfitLossAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX_9_UNREALIZED_PL_2018 || "0"),
                    0
                  )
                  .toFixed(2),
                CYOpenCntrctProfitLossAmt: records
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX_10_UNREALIZED_PL_2019 || "0"),
                    0
                  )
                  .toFixed(2),
                CntrctAggregateProfitLossAmt: records
                  .reduce(
                    (sum, r) => sum + parseFloat(r.BOX_11_AGGREGATE_PL || "0"),
                    0
                  )
                  .toFixed(2),
                // 8. BarterAmt
                BarterAmt: records
                  .reduce(
                    (sum, r) => sum + parseFloat(r.BOX_13_BARTERING || "0"),
                    0
                  )
                  .toFixed(2),
              },
            },
          },
          IRSubmission1Detail: {
            Form1099BDetail: form1099BDetails,
          },
        },
      ],
    },
  };

  try {
    const xml = builder.build(xmlStructure);
    return { xml, taxYear };
  } catch (error: any) {
    console.error("Error generating XML:", error);
    throw new Error(`Failed to generate XML: ${error.message}`);
  }
}

/**
 * Helper function to determine if state/local tax groups should be included.
 */
function shouldIncludeStateLocalTaxGrp(record: Record1099B): boolean {
  return !!(
    (record.BOX_15_STATE_ID1 &&
      parseFloat(record.BOX_16_STATE_TAX_WITHHELD1 || "0") > 0) ||
    (record.BOX_15_STATE_ID2 &&
      parseFloat(record.BOX_16_STATE_TAX_WITHHELD2 || "0") > 0)
  );
}

/**
 * Helper function to build state/local tax group arrays.
 */
function getStateLocalTaxGrp(record: Record1099B): any[] {
  const groups = [];
  if (
    record.BOX_15_STATE_ID1 &&
    parseFloat(record.BOX_16_STATE_TAX_WITHHELD1 || "0") > 0
  ) {
    groups.push({
      StateAbbreviationCd: record.RECIPIENT_STATE?.substring(0, 2),
      StateTaxGrp: {
        StateTaxWithheldAmt: parseAmount(record.BOX_16_STATE_TAX_WITHHELD1),
      },
    });
  }
  if (
    record.BOX_15_STATE_ID2 &&
    parseFloat(record.BOX_16_STATE_TAX_WITHHELD2 || "0") > 0
  ) {
    groups.push({
      StateAbbreviationCd: record.RECIPIENT_STATE?.substring(0, 2),
      StateTaxGrp: {
        StateTaxWithheldAmt: parseAmount(record.BOX_16_STATE_TAX_WITHHELD2),
      },
    });
  }
  return groups;
}
